"use client"

/**
 * INTEGRATION: Logo Carousel Component
 *
 * Premium infinite scroll with touch support:
 * - Smooth drag scrolling with momentum/inertia
 * - Seamless animation resume after drag
 * - GPU-accelerated transforms
 *
 * PERF: Uses plain <img> instead of Next.js <Image> to avoid srcSet bloat.
 * Logo files are 0.6-1.8KB PNGs — srcSet optimization is pointless.
 * REPEAT_COUNT=2 is sufficient for seamless loop (track width ~1864px covers ultra-wide).
 * MorphingImg uses 2 plain <img> + CSS opacity transition instead of Framer Motion.
 */
import { useRef, useCallback, useEffect, useState, memo } from "react"
import { T } from "gt-next"

interface LogoCarouselProps {
    variant?: "default" | "compact"
}

// Logo carousel - defined at module level for stable references
export const LOGOS = [
    { src: "/img/logos/logo_farfetch.png", alt: "Farfetch" },
    { src: "/img/logos/logo_mont_blanc.png", alt: "Mont Blanc" },
    { src: "/img/logos/logo_bonprix.png", alt: "Bonprix" },
    { src: "/img/logos/logo_birkenstock.png", alt: "Birkenstock" },
    { src: "/img/logos/logo_mixtiles.png", alt: "Mixtiles" },
    { src: "/img/logos/logo_sennheiser.png", alt: "Sennheiser" },
    { src: "/img/logos/logo_victorias_secret.png", alt: "Victoria's Secret" },
    { src: "/img/logos/logo_dbrand.png", alt: "dbrand" }
] as const

// Pre-computed logo groups for morph effect
const LOGO_GROUPS = LOGOS.map((logo, index) => [logo, LOGOS[(index + 4) % LOGOS.length]])

// Repeat tracks for seamless loop (2 is enough: 8 logos * 185px + gaps = ~1864px per track)
const REPEAT_COUNT = 2

// Morph timing - organic, desynchronized
const LOGO_DELAYS = [800, 1600, 2500, 3600, 4900, 6100, 7400, 8600]
const LOGO_INTERVALS = [3700, 4200, 3900, 4400, 3600, 4100, 3800, 4300]

// Animation constants
const GAP_MOBILE = 28
const GAP_DESKTOP = 48
const DURATION = 35 // seconds
const FRICTION = 0.95 // Momentum decay
const MIN_VELOCITY = 0.5 // Stop threshold

// Lightweight morphing logo: 2 plain <img> tags with CSS opacity transition
// Replaces MorphingLogo (Framer Motion + 2 Next.js <Image> with srcSet bloat)
const MorphingImg = memo(function MorphingImg({
    logos,
    initialDelay,
    morphInterval,
    width,
    height
}: {
    logos: readonly { src: string; alt: string }[]
    initialDelay: number
    morphInterval: number
    width: number
    height: number
}) {
    const [currentIndex, setCurrentIndex] = useState(0)

    useEffect(() => {
        if (logos.length <= 1) return

        let intervalId: ReturnType<typeof setInterval>

        const timeoutId = setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % logos.length)
            intervalId = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % logos.length)
            }, morphInterval)
        }, initialDelay)

        return () => {
            clearTimeout(timeoutId)
            if (intervalId) clearInterval(intervalId)
        }
    }, [initialDelay, morphInterval, logos.length])

    return (
        <div style={{ position: "relative", width, height, contain: "layout" }}>
            {logos.map((logo, index) => (
                <img
                    key={logo.src}
                    src={logo.src}
                    alt={logo.alt}
                    width={width}
                    height={height}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain opacity-80"
                    style={{
                        opacity: index === currentIndex ? 0.8 : 0,
                        transform: index === currentIndex ? "scale(1)" : "scale(0.92)",
                        transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                />
            ))}
        </div>
    )
})

export default function LogoCarousel({ variant = "default" }: LogoCarouselProps) {
    const sectionPadding = variant === "compact" ? "pt-10 pb-10 md:pt-12 md:pb-14" : "pt-16 pb-4 md:pt-16"

    const wrapperRef = useRef<HTMLDivElement>(null)
    const rafRef = useRef<number | null>(null)
    const trackWidthRef = useRef(0)
    const gapRef = useRef(GAP_MOBILE)

    const state = useRef({
        isDragging: false,
        startX: 0,
        currentX: 0,
        velocity: 0,
        lastX: 0,
        lastTime: 0,
        isAnimating: true
    })

    // Animation loop using requestAnimationFrame
    const startAutoScroll = useCallback(() => {
        const wrapper = wrapperRef.current
        if (!wrapper || !trackWidthRef.current) return

        const animate = () => {
            if (state.current.isDragging || !state.current.isAnimating) return

            // Move at constant speed (trackWidth / duration seconds)
            const speed = trackWidthRef.current / (DURATION * 60) // pixels per frame at 60fps
            state.current.currentX -= speed

            // Wrap around for seamless loop
            if (Math.abs(state.current.currentX) >= trackWidthRef.current) {
                state.current.currentX += trackWidthRef.current
            }

            wrapper.style.transform = `translate3d(${state.current.currentX}px, 0, 0)`
            rafRef.current = requestAnimationFrame(animate)
        }

        rafRef.current = requestAnimationFrame(animate)
    }, [])

    // Momentum animation after drag
    const animateMomentum = useCallback(() => {
        const wrapper = wrapperRef.current
        if (!wrapper || !trackWidthRef.current) return

        const animate = () => {
            if (state.current.isDragging) return

            // Apply friction
            state.current.velocity *= FRICTION

            // Stop if velocity is too low
            if (Math.abs(state.current.velocity) < MIN_VELOCITY) {
                state.current.isAnimating = true
                startAutoScroll()
                return
            }

            // Apply velocity
            state.current.currentX += state.current.velocity

            // Wrap around for seamless loop
            const trackWidth = trackWidthRef.current
            if (state.current.currentX > 0) {
                state.current.currentX -= trackWidth
            } else if (state.current.currentX < -trackWidth) {
                state.current.currentX += trackWidth
            }

            wrapper.style.transform = `translate3d(${state.current.currentX}px, 0, 0)`
            rafRef.current = requestAnimationFrame(animate)
        }

        rafRef.current = requestAnimationFrame(animate)
    }, [startAutoScroll])

    // Start auto-scroll on mount
    useEffect(() => {
        const wrapper = wrapperRef.current
        if (!wrapper) return

        // Detect screen size for responsive gap
        const isMobile = window.innerWidth < 768
        gapRef.current = isMobile ? GAP_MOBILE : GAP_DESKTOP

        // Calculate track width after render
        const calculateAndStart = () => {
            const firstTrack = wrapper.querySelector("[data-track]") as HTMLElement
            if (firstTrack && firstTrack.offsetWidth > 0) {
                trackWidthRef.current = firstTrack.offsetWidth + gapRef.current
                state.current.isAnimating = true
                startAutoScroll()
            } else {
                // Retry if not ready
                requestAnimationFrame(calculateAndStart)
            }
        }

        // Start after a frame to ensure layout is complete
        requestAnimationFrame(calculateAndStart)

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [startAutoScroll])

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)

        const touch = e.touches[0]
        state.current.isDragging = true
        state.current.isAnimating = false
        state.current.startX = touch.clientX - state.current.currentX
        state.current.lastX = touch.clientX
        state.current.lastTime = Date.now()
        state.current.velocity = 0
    }, [])

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!state.current.isDragging) return

        const touch = e.touches[0]
        const now = Date.now()
        const dt = now - state.current.lastTime

        // Calculate new position
        const newX = touch.clientX - state.current.startX

        // Calculate velocity (pixels per frame, assuming 16ms frames)
        if (dt > 0) {
            state.current.velocity = ((touch.clientX - state.current.lastX) / dt) * 16
        }

        state.current.currentX = newX
        state.current.lastX = touch.clientX
        state.current.lastTime = now

        // Apply transform
        const wrapper = wrapperRef.current
        if (wrapper) {
            wrapper.style.transform = `translate3d(${newX}px, 0, 0)`
        }
    }, [])

    const handleTouchEnd = useCallback(() => {
        if (!state.current.isDragging) return

        state.current.isDragging = false

        // Normalize position within bounds
        const trackWidth = trackWidthRef.current
        if (trackWidth > 0) {
            while (state.current.currentX > 0) {
                state.current.currentX -= trackWidth
            }
            while (state.current.currentX < -trackWidth) {
                state.current.currentX += trackWidth
            }
        }

        // Start momentum animation
        animateMomentum()
    }, [animateMomentum])

    // Mouse handlers for desktop drag
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        if (rafRef.current) cancelAnimationFrame(rafRef.current)

        state.current.isDragging = true
        state.current.isAnimating = false
        state.current.startX = e.clientX - state.current.currentX
        state.current.lastX = e.clientX
        state.current.lastTime = Date.now()
        state.current.velocity = 0

        // Add global mouse listeners for drag outside element
        document.addEventListener("mousemove", handleMouseMove)
        document.addEventListener("mouseup", handleMouseUp)
    }, [])

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!state.current.isDragging) return

        const now = Date.now()
        const dt = now - state.current.lastTime
        const newX = e.clientX - state.current.startX

        if (dt > 0) {
            state.current.velocity = ((e.clientX - state.current.lastX) / dt) * 16
        }

        state.current.currentX = newX
        state.current.lastX = e.clientX
        state.current.lastTime = now

        const wrapper = wrapperRef.current
        if (wrapper) {
            wrapper.style.transform = `translate3d(${newX}px, 0, 0)`
        }
    }, [])

    const handleMouseUp = useCallback(() => {
        if (!state.current.isDragging) return

        state.current.isDragging = false
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)

        const trackWidth = trackWidthRef.current
        if (trackWidth > 0) {
            while (state.current.currentX > 0) {
                state.current.currentX -= trackWidth
            }
            while (state.current.currentX < -trackWidth) {
                state.current.currentX += trackWidth
            }
        }

        animateMomentum()
    }, [animateMomentum, handleMouseMove])

    // Wheel handler for trackpad horizontal scroll
    const handleWheel = useCallback((e: React.WheelEvent) => {
        // Only handle horizontal scroll (trackpad swipe)
        if (Math.abs(e.deltaX) < 5) return

        e.preventDefault()
        if (rafRef.current) cancelAnimationFrame(rafRef.current)

        state.current.isAnimating = false
        state.current.currentX -= e.deltaX

        // Wrap around for seamless loop
        const trackWidth = trackWidthRef.current
        if (trackWidth > 0) {
            if (state.current.currentX > 0) {
                state.current.currentX -= trackWidth
            } else if (state.current.currentX < -trackWidth) {
                state.current.currentX += trackWidth
            }
        }

        const wrapper = wrapperRef.current
        if (wrapper) {
            wrapper.style.transform = `translate3d(${state.current.currentX}px, 0, 0)`
        }

        // Resume auto-scroll after a delay
        state.current.velocity = -e.deltaX * 0.5
        animateMomentum()
    }, [animateMomentum])

    return (
        <section className={`logo-carousel-section relative flex flex-col items-center justify-center overflow-hidden bg-white px-3 md:px-10 ${sectionPadding}`} aria-label="Trusted by leading brands">
            <div className="flex w-full max-w-[1136px] flex-col items-center gap-6">
                <h2 className="logo-carousel-title text-center text-[28px] font-semibold leading-[34px] text-[rgb(20,20,20)] md:text-[35px] md:leading-[40.25px]"><T>Trusted by brands of all sizes</T></h2>

                <div
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onWheel={handleWheel}
                    className="relative mt-2 h-[80px] w-full cursor-grab overflow-hidden select-none active:cursor-grabbing md:mt-4 md:h-[75px]"
                    style={{
                        maskImage: "linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent)",
                        WebkitMaskImage: "linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent)",
                        touchAction: "pan-y"
                    }}>
                    {/* Animated wrapper - this is what we translate */}
                    <div
                        ref={wrapperRef}
                        className="flex items-center gap-7 md:gap-12"
                        style={{
                            willChange: "transform",
                            transform: "translate3d(0, 0, 0)"
                        }}>
                        {Array.from({ length: REPEAT_COUNT }).map((_, trackIndex) => (
                            <div
                                key={trackIndex}
                                data-track
                                className="flex shrink-0 items-center gap-7 md:gap-12"
                                aria-hidden={trackIndex > 0}>
                                {LOGO_GROUPS.map((logoGroup, index) => (
                                    <div key={`logo-${index}`} className="relative h-[80px] w-[185px] flex-shrink-0 md:h-[75px] md:w-[176px]">
                                        <MorphingImg
                                            logos={logoGroup}
                                            initialDelay={LOGO_DELAYS[index]}
                                            morphInterval={LOGO_INTERVALS[index]}
                                            width={185}
                                            height={80}
                                        />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
